/* eslint-disable react/display-name */
'use client';

import Close from '@hc-frontend/core-icons/kit/close';
import SvgPhoneInTalk from '@hc-frontend/core-icons/kit/phone-in-talk';
import type { ButtonClickEvent } from '@hc-frontend/core-ui';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DialogCloseButton,
  Link,
  useEventBus,
  useTrackingContexts,
} from '@hc-frontend/core-ui';
import { toPhoneE164, toUSPhone } from '@hc-frontend/core-utils-format';
import type { ComponentImpressionEvent } from '@hc-frontend/features-tracking';
import {
  CallCta,
  type SendMetricEvent,
  type SendMetricEventTarget,
} from '@hc-frontend/shells-shared';
import Image from 'next/image';
import type { MouseEventHandler, ReactNode, RefObject } from 'react';
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { ConnectMeButton } from '../../atoms/connect-me-button';
import { TypConnectMeHolder } from '../../molecules/typ-connect-me-holder/typ-connect-me-holder';
import type {
  TypConnectMeModalClientExposedRef,
  TypConnectMeModalClientProps,
} from './typ-connect-me-modal.types';
import { TypCountdown } from './typ-countdown';

export function TypLinkEnroll({
  children,
  ...props
}: React.LinkHTMLAttributes<HTMLAnchorElement> & { children: ReactNode }) {
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <a
      {...props}
      onClick={(event) => {
        eventBus.publish<ButtonClickEvent>({
          id: 'quotes-enroll',
          topic: 'core.ui.button.click',
          source: event.currentTarget,
          contexts,
        });
      }}
    >
      {children}
    </a>
  );
}

const TypConnectMeModalContext =
  createContext<RefObject<TypConnectMeModalClientExposedRef> | null>(null);

export function TypConnectMeModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const ref = useRef<TypConnectMeModalClientExposedRef>(null);

  return (
    <TypConnectMeModalContext.Provider value={ref}>
      {children}
    </TypConnectMeModalContext.Provider>
  );
}

export function useTypConnectMeModalContext() {
  return useContext(TypConnectMeModalContext);
}

export function TypConnectMeModalTrigger({
  children,
}: {
  children: ReactNode;
}) {
  const ref = useTypConnectMeModalContext();
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();

  const handleOpenModal: MouseEventHandler<HTMLButtonElement> = (event) => {
    ref?.current?.showModal();

    eventBus.publish<ButtonClickEvent>({
      id: 'connect-me-modal-trigger',
      topic: 'core.ui.button.click',
      source: event.currentTarget,
      contexts,
    });
  };

  return (
    <button type="button" onClick={handleOpenModal}>
      {children}
    </button>
  );
}

export const TypConnectMeModalClientContent = forwardRef<
  TypConnectMeModalClientExposedRef,
  TypConnectMeModalClientProps
>(
  (
    {
      experience,
      phoneNumber,
      placement,
      product,
      sessionId,
      marketplaceStaticBaseUrl,
      autoTrigger = true,
      autoTriggerDelay = 0,
      customImage,
      customHeading,
      variant = 'with-logo',
      primaryCampaign,
      secondaryCampaign,
    },
    ref,
  ) => {
    const eventBus = useEventBus();
    const contexts = useTrackingContexts();
    const internalRef = useRef<HTMLDialogElement>(null);
    const connectMeModalId = 'connect-me-modal';
    const connectMeModalCloseId = 'connect-me-modal-close';
    const connectMeCountdownId = 'connect-me-countdown';

    const [isOpen, setIsOpen] = useState(false);
    const [isInitCall, setIsInitCall] = useState(false);

    const connectMeDestinationPhoneNumber = toPhoneE164(
      primaryCampaign.phoneNumber,
    );

    const handleOpen = useCallback(() => {
      if (internalRef.current) {
        setIsOpen(true);
        internalRef.current.showModal();

        eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
          id: connectMeModalId,
          topic: 'metric.client.message',
          source: connectMeModalId,
          metric: {
            name: 'connect-me',
            tags: {
              action: 'Display',
              experience,
              product,
              placement: primaryCampaign.placement,
            },
          },
          contexts,
        });

        eventBus.publish<ComponentImpressionEvent>({
          id: connectMeModalCloseId,
          topic: 'core.ui.component.impression',
          source: internalRef.current,
          componentImpression: {
            component: connectMeModalId,
            action: 'display',
            category: placement,
            product,
          },
          contexts,
        });
      }
    }, [
      eventBus,
      contexts,
      placement,
      primaryCampaign.placement,
      experience,
      product,
    ]);

    const handleOnClickConnectMe = () => {
      setIsInitCall(true);
    };

    const handleOnCountdownClose = () => {
      if (internalRef.current) {
        eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
          id: connectMeCountdownId,
          topic: 'metric.client.message',
          source: connectMeCountdownId,
          metric: {
            name: 'connect-me',
            tags: {
              action: 'Countdown Close',
              experience,
              product,
              placement: primaryCampaign.placement,
            },
          },
          contexts,
        });
        eventBus.publish<ComponentImpressionEvent>({
          id: connectMeModalCloseId,
          topic: 'core.ui.component.impression',
          source: internalRef.current,
          componentImpression: {
            component: connectMeModalId,
            action: 'hide',
            category: placement,
            product,
          },
          contexts,
        });
        setIsOpen(false);
        internalRef.current.close();
      }
    };

    const handleOnClickClose = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (internalRef.current) {
        eventBus.publish<ButtonClickEvent>({
          id: connectMeModalCloseId,
          topic: 'core.ui.button.click',
          source: event.currentTarget,
          contexts,
        });

        eventBus.publish<ComponentImpressionEvent>({
          id: connectMeModalCloseId,
          topic: 'core.ui.component.impression',
          source: internalRef.current,
          componentImpression: {
            component: connectMeModalId,
            action: 'hide',
            category: placement,
            product,
          },
          contexts,
        });

        eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
          id: connectMeModalCloseId,
          topic: 'metric.client.message',
          source: connectMeModalCloseId,
          metric: {
            name: 'connect-me',
            tags: {
              action: 'Close',
              experience: 'modal-connectme',
              product,
              placement: primaryCampaign.placement,
            },
          },
          contexts,
        });

        setIsOpen(false);
        internalRef.current.close();
      }
    };

    // Automatically open the modal
    useEffect(() => {
      if (autoTrigger) {
        setTimeout(handleOpen, autoTriggerDelay);
      }
    }, [autoTrigger, autoTriggerDelay, handleOpen]);

    // Expose open method to pragmatically show the modal (e.g. a click action)
    useImperativeHandle(
      ref,
      () => ({
        showModal: () => handleOpen(),
      }),
      [handleOpen],
    );

    const [cardHeaderText, setCardHeaderText] = useState(
      'A licensed insurance agent just became available!',
    );

    if (variant === 'multiple-options') {
      return (
        <div className="hc-imposter-layout">
          <dialog
            ref={internalRef}
            className="max-w-[500px] overflow-visible rounded-lg bg-gray-0 shadow-lg backdrop:bg-[rgba(0,0,0,0.65)]"
          >
            <div className="relative flex w-full justify-center overflow-visible">
              {customImage ?? (
                <>
                  <div className="absolute left-1/2 top-0 aspect-square w-full max-w-[300px] -translate-x-1/2 -translate-y-1/2 tablet:max-w-[380px]">
                    <Image
                      src={`${marketplaceStaticBaseUrl}/marketplace/images/call-licensed-agent-woman.png`}
                      alt="Speak to a licensed agent"
                      fill
                    />
                  </div>
                  <div className="aspect-[3/1] w-full max-w-[300px] tablet:aspect-[2/1]"></div>
                </>
              )}

              <DialogCloseButton
                className="absolute right-16-16 top-16-16 outline-none"
                onClick={handleOnClickClose}
              >
                <Close className="size-16-16 text-gray-200" />
              </DialogCloseButton>
            </div>
            <div className="px-24-24 py-32-32 text-center text-text-950">
              {customHeading ?? (
                <h1 className="hc-typography--heading-three mb-24-32 text-balance">
                  A licensed insurance agent just became available!
                </h1>
              )}

              {phoneNumber ? (
                <>
                  {!isInitCall ? (
                    <p
                      className="hc-typography--paragraph-md mb-32-32 text-balance"
                      data-testid="initial-connect-me-message"
                    >
                      To connect, click{' '}
                      <span className="font-bold">Connect Me!</span> and pick up
                      the phone when it rings. Your spot will go to another
                      caller in{' '}
                      <span className="font-bold">
                        {isOpen ? (
                          <TypCountdown onEnd={handleOnCountdownClose} />
                        ) : null}
                      </span>{' '}
                      seconds.
                    </p>
                  ) : null}

                  <div
                    className={`${isInitCall ? 'border border-dashed border-gray-50 bg-primary-background-50 p-12-12' : ''} mb-8-8 w-full rounded-md`}
                  >
                    <ConnectMeButton
                      className="w-full"
                      id="typ-connect-me"
                      variant="primary-card"
                      type="button"
                      experience={experience}
                      phoneNumber={phoneNumber}
                      destinationPhoneNumber={connectMeDestinationPhoneNumber}
                      callType={primaryCampaign.callType}
                      placement={primaryCampaign.placement}
                      sessionId={sessionId}
                      product={product}
                      isSendingDisplayMetric={false}
                      onClick={handleOnClickConnectMe}
                      advertiser={primaryCampaign.advertiser}
                    >
                      <p className="mx-auto">Connect Me!</p>
                    </ConnectMeButton>
                  </div>

                  {secondaryCampaign ? (
                    <p>
                      Or call at{' '}
                      <Link
                        className="font-bold"
                        href={secondaryCampaign.href}
                        id="connect-me-click-to-call"
                        data-call-call-type={secondaryCampaign.callType}
                        data-call-placement={secondaryCampaign.placement}
                        data-call-product={secondaryCampaign.product}
                        data-call-phone={secondaryCampaign.href}
                        data-call-advertiser={secondaryCampaign.advertiser}
                      >
                        {toUSPhone(secondaryCampaign.phoneNumber)}
                      </Link>
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  {!isInitCall ? (
                    <p className="hc-typography--paragraph-md mb-32-32 text-balance">
                      To speak with an agent, click{' '}
                      <span className="font-bold">Call Now!</span> Your spot
                      will go to another caller in{' '}
                      <span className="font-bold">
                        {isOpen ? (
                          <TypCountdown onEnd={handleOnCountdownClose} />
                        ) : null}
                      </span>{' '}
                      seconds.
                    </p>
                  ) : null}

                  {secondaryCampaign ? (
                    <CallCta
                      phoneCampaign={secondaryCampaign}
                      className="flex w-full flex-wrap items-center justify-center gap-12-12"
                    >
                      Call Now!
                    </CallCta>
                  ) : null}
                </>
              )}
            </div>
          </dialog>
        </div>
      );
    }

    if (variant === 'simplified') {
      return (
        <div className="hc-imposter-layout">
          <dialog
            ref={internalRef}
            className="max-w-[800px] rounded-md bg-gray-0 text-center text-text-950 shadow-lg backdrop:bg-[rgba(0,0,0,0.65)]"
          >
            <Card className="w-full">
              <CardHeader className="hc-icon-layout--right relative justify-center px-32-60 py-4-16">
                {cardHeaderText}
                <DialogCloseButton onClick={handleOnClickClose} />
              </CardHeader>
              <CardBody className="hc-stack-layout--large">
                {isInitCall ? (
                  <TypConnectMeHolder
                    experience={experience}
                    phoneNumber={phoneNumber}
                    destinationPhoneNumber={connectMeDestinationPhoneNumber}
                    placement={placement}
                    sessionId={sessionId}
                    product={product}
                    setCardHeaderText={setCardHeaderText}
                    advertiser={primaryCampaign.advertiser}
                  />
                ) : (
                  <>
                    <p
                      className="hc-typography--paragraph-md"
                      data-testid="initial-connect-me-message"
                    >
                      To connect, click{' '}
                      <span className="font-bold underline">Connect Me</span>{' '}
                      and pick up the phone when it rings
                    </p>
                    <span className="hc-typography--title-case">
                      {isOpen ? (
                        <TypCountdown onEnd={handleOnCountdownClose} />
                      ) : null}
                    </span>
                    <h3 className="hc-typography--heading-three">seconds</h3>
                    <p className="hc-typography--paragraph-md">
                      Your spot will go to another caller if you do not answer
                    </p>
                  </>
                )}

                {isInitCall ? (
                  <Button
                    id="typ-connect-me"
                    variant="primary-card"
                    type="button"
                    className="w-full"
                    onClick={handleOnClickClose}
                  >
                    <p className="mx-auto">Close</p>
                  </Button>
                ) : (
                  <ConnectMeButton
                    className="w-full"
                    id="typ-connect-me"
                    variant="primary-card"
                    type="button"
                    experience={experience}
                    phoneNumber={phoneNumber}
                    destinationPhoneNumber={connectMeDestinationPhoneNumber}
                    callType={primaryCampaign.callType}
                    placement={primaryCampaign.placement}
                    sessionId={sessionId}
                    product={product}
                    isSendingDisplayMetric={false}
                    onClick={handleOnClickConnectMe}
                    advertiser={primaryCampaign.advertiser}
                  >
                    <p className="mx-auto">Connect Me</p>
                  </ConnectMeButton>
                )}
              </CardBody>
            </Card>
          </dialog>
        </div>
      );
    }

    return (
      <div className="hc-imposter-layout">
        <dialog
          ref={internalRef}
          className="max-w-[800px] rounded-md bg-gray-0 text-center shadow-lg backdrop:bg-[rgba(0,0,0,0.65)]"
        >
          <div className="flex w-full justify-center bg-svg-blue-900 pt-16-16">
            <div className="hc-typography--heading-one text-text-0">
              <DialogCloseButton onClick={handleOnClickClose} />
            </div>
            <div className="mt-40-40 h-[140px] w-[280px] rounded-t-[280px] bg-gray-0">
              <Image
                className="translate-x-[40%] translate-y-[8%]"
                src={`${marketplaceStaticBaseUrl}/marketplace/images/typ/chris-call.svg`}
                alt="Speak to a licensed agent"
                width={202}
                height={152}
              />
            </div>
          </div>

          <div className="px-60-60 pb-16-16 pt-24-24">
            <h1 className="hc-typography--heading-four">
              A licensed insurance agent just became available!
            </h1>

            {isInitCall ? null : (
              <p
                className="hc-typography--paragraph-md"
                data-testid="initial-connect-me-message"
              >
                To connect, click <span className="font-bold">Connect Me!</span>{' '}
                and pick up the phone when it rings. Your spot will go to
                another caller in{' '}
                <span className="font-bold">
                  {isOpen ? (
                    <TypCountdown onEnd={handleOnCountdownClose} />
                  ) : null}
                </span>{' '}
                seconds.
              </p>
            )}

            <ConnectMeButton
              className="hc-icon-layout--left mt-16-16"
              id="typ-connect-me"
              variant="primary-card"
              type="button"
              experience={experience}
              phoneNumber={phoneNumber}
              destinationPhoneNumber={connectMeDestinationPhoneNumber}
              placement={primaryCampaign.placement}
              callType={primaryCampaign.callType}
              sessionId={sessionId}
              product={product}
              isSendingDisplayMetric={false}
              onClick={handleOnClickConnectMe}
              advertiser={primaryCampaign.advertiser}
            >
              <SvgPhoneInTalk />
              Connect Me!
            </ConnectMeButton>
          </div>
        </dialog>
      </div>
    );
  },
);

export function TypConnectMeModalClient(props: TypConnectMeModalClientProps) {
  const ref = useTypConnectMeModalContext();
  return <TypConnectMeModalClientContent {...props} ref={ref} />;
}
